import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { apiClient } from '../../services/api';

const LLMSettings = () => {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [formData, setFormData] = useState({
    llm_provider: 'gemini',
    gemini_api_key: '',
    deepseek_api_key: '',
    openai_api_key: '',
    openrouter_api_key: '',
    gemini_model: 'gemini-1.5-flash',
    deepseek_model: 'deepseek-chat',
    openai_model: 'gpt-3.5-turbo',
    openrouter_model: 'anthropic/claude-3-haiku',
    llm_enabled: false,
    max_tokens: '2048',
    temperature: '0.1'
  });

  useEffect(() => {
    loadSettings();
  }, []);

  // Provider configurations
  const providers = [
    { id: 'gemini', name: 'Google Gemini', description: 'Google\'s Gemini AI models' },
    { id: 'deepseek', name: 'DeepSeek', description: 'DeepSeek AI models' },
    { id: 'openai', name: 'OpenAI', description: 'OpenAI GPT models' },
    { id: 'openrouter', name: 'OpenRouter', description: 'Access to multiple AI providers' }
  ];

  // Model options for each provider
  const modelOptions = {
    gemini: [
      { id: 'gemini-1.5-flash', label: 'Gemini 1.5 Flash (Recommended)' },
      { id: 'gemini-1.5-flash-8b', label: 'Gemini 1.5 Flash 8B' },
      { id: 'gemini-1.5-pro', label: 'Gemini 1.5 Pro' },
    ],
    deepseek: [
      { id: 'deepseek-chat', label: 'DeepSeek Chat (Recommended)' },
      { id: 'deepseek-coder', label: 'DeepSeek Coder' },
    ],
    openai: [
      { id: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo' },
      { id: 'gpt-4', label: 'GPT-4' },
      { id: 'gpt-4-turbo', label: 'GPT-4 Turbo (Recommended)' },
    ],
    openrouter: [
      { id: 'anthropic/claude-3-haiku', label: 'Claude 3 Haiku (Recommended)' },
      { id: 'anthropic/claude-3-sonnet', label: 'Claude 3 Sonnet' },
      { id: 'meta-llama/llama-3-8b-instruct', label: 'Llama 3 8B' },
      { id: 'meta-llama/llama-3-70b-instruct', label: 'Llama 3 70B' },
    ]
  };

  // API key links for each provider
  const apiKeyLinks = {
    gemini: { url: 'https://makersuite.google.com/app/apikey', text: 'Google AI Studio' },
    deepseek: { url: 'https://platform.deepseek.com/', text: 'DeepSeek Platform' },
    openai: { url: 'https://platform.openai.com/api-keys', text: 'OpenAI Platform' },
    openrouter: { url: 'https://openrouter.ai/keys', text: 'OpenRouter Dashboard' }
  };

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/admin/llm-settings');
      const settingsData = response.data.settings;

      setSettings(settingsData);
      // Note: Using static model options defined in component for now
      // Server-provided models could be used in future for dynamic discovery
      
      // Populate form data
      const formValues = {};
      settingsData.forEach(setting => {
        if (setting.key === 'llm_enabled') {
          formValues[setting.key] = setting.value === 'true';
        } else {
          formValues[setting.key] = setting.value === '***CONFIGURED***' ? '' : setting.value;
        }
      });
      
      setFormData(prev => ({
        ...prev,
        ...formValues,
        // Set defaults for provider and models if not configured
        llm_provider: formValues.llm_provider || 'gemini',
        gemini_model: formValues.gemini_model || 'gemini-1.5-flash',
        deepseek_model: formValues.deepseek_model || 'deepseek-chat',
        openai_model: formValues.openai_model || 'gpt-3.5-turbo',
        openrouter_model: formValues.openrouter_model || 'anthropic/claude-3-haiku',
      }));
    } catch (error) {
      console.error('Failed to load LLM settings:', error);
      toast.error('Failed to load LLM settings');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      const updateData = { ...formData };

      // Only send API keys if they've been changed
      const providerKeys = ['gemini_api_key', 'deepseek_api_key', 'openai_api_key', 'openrouter_api_key'];
      providerKeys.forEach(key => {
        if (formData[key] === '') {
          delete updateData[key];
        }
      });
      
      await apiClient.put('/admin/llm-settings', updateData);
      toast.success('LLM settings updated successfully');
      
      // Reload settings to get updated values
      await loadSettings();
    } catch (error) {
      console.error('Failed to update LLM settings:', error);
      toast.error(`Failed to update settings: ${error.response?.data?.message || error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    try {
      setTesting(true);
      const response = await apiClient.post('/admin/llm-settings/test');
      
      if (response.data.success) {
        toast.success('LLM connection test successful!');
        console.log('Test result:', response.data.testResult);
      } else {
        toast.error(`Test failed: ${response.data.message}`);
      }
    } catch (error) {
      console.error('LLM test failed:', error);
      toast.error(`Test failed: ${error.response?.data?.message || error.message}`);
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">LLM Settings</h2>
        <p className="text-gray-600 mt-2">
          Configure {providers.find(p => p.id === formData.llm_provider)?.name || 'AI'} for intelligent invoice data extraction
        </p>
      </div>

      <div className="space-y-6">
        {/* Enable/Disable LLM */}
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Enable LLM Processing</h3>
            <p className="text-sm text-gray-600">
              Use AI to extract and format invoice data automatically
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={formData.llm_enabled}
              onChange={(e) => handleInputChange('llm_enabled', e.target.checked)}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>

        {/* Provider Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            AI Provider
          </label>
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={formData.llm_provider}
            onChange={(e) => handleInputChange('llm_provider', e.target.value)}
          >
            {providers.map(provider => (
              <option key={provider.id} value={provider.id}>
                {provider.name} - {provider.description}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Choose which AI provider to use for invoice data extraction
          </p>
        </div>

        {/* API Key */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {providers.find(p => p.id === formData.llm_provider)?.name || 'API'} API Key
          </label>
          <input
            type="password"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder={settings.find(s => s.key === `${formData.llm_provider}_api_key`)?.value === '***CONFIGURED***' ? 'API Key is configured' : `Enter your ${providers.find(p => p.id === formData.llm_provider)?.name || 'API'} API key`}
            value={formData[`${formData.llm_provider}_api_key`]}
            onChange={(e) => handleInputChange(`${formData.llm_provider}_api_key`, e.target.value)}
          />
          <p className="text-xs text-gray-500 mt-1">
            Get your API key from <a href={apiKeyLinks[formData.llm_provider]?.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{apiKeyLinks[formData.llm_provider]?.text}</a>
          </p>
        </div>

        {/* Model Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {providers.find(p => p.id === formData.llm_provider)?.name || 'AI'} Model
          </label>
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={formData[`${formData.llm_provider}_model`]}
            onChange={(e) => handleInputChange(`${formData.llm_provider}_model`, e.target.value)}
          >
            {modelOptions[formData.llm_provider]?.map(m => (
              <option key={m.id} value={m.id}>{m.label}</option>
            ))}
          </select>
        </div>

        {/* Advanced Settings */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Max Tokens
            </label>
            <input
              type="number"
              min="100"
              max="8192"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={formData.max_tokens}
              onChange={(e) => handleInputChange('max_tokens', e.target.value)}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Temperature
            </label>
            <input
              type="number"
              min="0"
              max="1"
              step="0.1"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={formData.temperature}
              onChange={(e) => handleInputChange('temperature', e.target.value)}
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-4 pt-4">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
          
          <button
            onClick={handleTest}
            disabled={testing || !formData.llm_enabled}
            className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {testing ? 'Testing...' : 'Test Connection'}
          </button>
        </div>

        {/* Status Information */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-blue-900 mb-2">How it works:</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Users upload invoice files (PDF, images, Excel, CSV)</li>
            <li>• System extracts raw data using OCR and parsing</li>
            <li>• When creating invoice, LLM analyzes the data</li>
            <li>• AI formats data according to FBR requirements</li>
            <li>• Only buyer information and product details are extracted</li>
            <li>• Seller information comes from user profile</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default LLMSettings;
